import React, { useEffect, useState } from 'react';
import { X, Trash2, MessageSquare } from 'lucide-react';
import { ProBadge } from '@/components/billing/UpgradeModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BRAND_COLOUR, SLOT_LABEL } from './useSchedule';

const EMPTY = { type: 'work', project_id: '', stage_id: '', label: '', start_date: '', end_date: '', slot: 'day', start_time: '08:00', end_time: '12:00', crew_ids: [], note: '' };

export function BlockModal({ open, initial, projects, crew, lockProject, onSave, onDelete, onClose, onTextCustomer }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, ...initial, end_date: initial?.end_date || initial?.start_date || '', crew_ids: initial?.crew_ids || [] });
  }, [open, initial]);

  if (!open) return null;
  const project = projects.find((p) => p.id === f.project_id);
  const stages = project?.stages || [];
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, project_id: f.type === 'work' ? f.project_id || null : null, stage_id: f.type === 'work' ? f.stage_id || null : null,
        end_date: f.end_date || f.start_date, start_time: f.slot === 'hours' ? f.start_time : null, end_time: f.slot === 'hours' ? f.end_time : null };
      await onSave(body);
      onClose();
    } catch { /* toast shown by hook */ } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="block-modal">
      <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[95dvh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900">{isEdit ? 'Edit block' : 'Add to calendar'}</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-testid="block-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="block-type">
            {[['work', 'Work on a project'], ['unavailable', 'Unavailable / day off']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setF((x) => ({ ...x, type: v }))} data-testid={`block-type-${v}`}
                className={`text-sm font-semibold px-3 py-2 rounded-lg border-2 ${f.type === v ? 'border-[#006400] bg-[#006400]/10 text-[#006400]' : 'border-gray-200 text-gray-600'}`}>{l}</button>
            ))}
          </div>

          {f.type === 'work' ? (
            <>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Project</Label>
                <select value={f.project_id} onChange={(e) => setF((x) => ({ ...x, project_id: e.target.value, stage_id: '' }))} disabled={lockProject} required
                  className="mt-1 w-full border-2 rounded-lg p-2.5 text-sm bg-white" data-testid="block-project">
                  <option value="">Choose a project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name}{p.reference ? ` · ${p.reference}` : ''}{p.customer_address ? ` · ${p.customer_address}` : ''}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Part of the install</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="block-stages">
                  {stages.map((s) => (
                    <button key={s.id} type="button" onClick={() => setF((x) => ({ ...x, stage_id: s.id, label: s.name }))} data-testid={`block-stage-${s.id}`}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-full border-2 ${f.stage_id === s.id ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 bg-white'}`}
                      style={f.stage_id === s.id ? { background: BRAND_COLOUR[project?.service_type] || '#006400' } : {}}>{s.name}</button>
                  ))}
                </div>
                <Input value={f.label} onChange={(e) => setF((x) => ({ ...x, label: e.target.value, stage_id: stages.some((s) => s.name === e.target.value) ? x.stage_id : '' }))}
                  placeholder="…or type your own, e.g. Site visit, Snagging" className="mt-2" data-testid="block-label" />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs font-semibold text-gray-600">Reason</Label>
              <Input value={f.label} onChange={set('label')} placeholder="e.g. Holiday, Van MOT, Rain day" className="mt-1" data-testid="block-label" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-gray-600">From</Label><Input type="date" required value={f.start_date} onChange={set('start_date')} className="mt-1" data-testid="block-start" /></div>
            <div><Label className="text-xs font-semibold text-gray-600">To</Label><Input type="date" value={f.end_date} min={f.start_date} onChange={set('end_date')} className="mt-1" data-testid="block-end" /></div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">Time</Label>
            <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid="block-slot">
              {Object.entries(SLOT_LABEL).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setF((x) => ({ ...x, slot: v }))} data-testid={`block-slot-${v}`}
                  className={`text-xs font-semibold px-2 py-2 rounded-lg border-2 ${f.slot === v ? 'border-[#27292E] bg-[#27292E] text-white' : 'border-gray-200 text-gray-600'}`}>{l}</button>
              ))}
            </div>
            {f.slot === 'hours' && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Input type="time" value={f.start_time || ''} onChange={set('start_time')} data-testid="block-start-time" />
                <Input type="time" value={f.end_time || ''} onChange={set('end_time')} data-testid="block-end-time" />
              </div>
            )}
          </div>

          {crew.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-gray-600">Who's on it</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="block-crew">
                {crew.filter((c) => c.active !== false).map((c) => {
                  const on = f.crew_ids.includes(c.id);
                  return (
                    <button key={c.id} type="button" data-testid={`block-crew-${c.id}`}
                      onClick={() => setF((x) => ({ ...x, crew_ids: on ? x.crew_ids.filter((i) => i !== c.id) : [...x.crew_ids, c.id] }))}
                      className={`text-xs font-semibold px-2.5 py-1.5 rounded-full border-2 flex items-center gap-1.5 ${on ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 bg-white'}`}
                      style={on ? { background: c.colour } : {}}>
                      <span className="w-2 h-2 rounded-full" style={{ background: on ? '#fff' : c.colour }} />{c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold text-gray-600">Note (optional)</Label>
            <textarea value={f.note} onChange={set('note')} rows={2} className="mt-1 w-full border-2 rounded-lg p-2.5 text-sm" placeholder="Skip arriving 7:30, bring the plate compactor…" data-testid="block-note" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex gap-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}>
          {isEdit && onDelete && (
            <Button type="button" variant="outline" onClick={async () => { await onDelete(initial.id); onClose(); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="block-delete"><Trash2 className="w-4 h-4" /></Button>
          )}
          {isEdit && initial?.type === 'work' && onTextCustomer && (
            <Button type="button" variant="outline" onClick={() => onTextCustomer(initial.id)} className="border-[#006400]/40 text-[#006400] hover:bg-[#006400]/10" data-testid="block-text-customer">
              <MessageSquare className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Text customer</span>{!onTextCustomer.allowed && <ProBadge className="ml-1.5" />}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-testid="block-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} className="flex-1 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="block-save">{busy ? 'Saving…' : isEdit ? 'Save' : 'Add'}</Button>
        </div>
      </form>
    </div>
  );
}
